// The three bets on today's feed with the strongest evidence behind them, and
// what to do about each one.
//
// Today is a timeline: everything the pad printed, in the order it starts. That
// is the right shape for reading a morning and the wrong shape for the question
// a punter actually arrives with, which is "what should I bet". So this ranks
// the same tickets — no new arithmetic, no second engine, nothing fetched — and
// says three things about each of the top three: WHY, HOW, and WHEN.
//
// WHAT GETS IN, AND WHAT DOES NOT.
//
//   A model strategy needs a PROVEN fit for every sport it has a leg in. That
//   is the same gate `runStrategy` applies before printing the ticket at all,
//   restated here because this sheet is the app's loudest claim and a claim
//   that leans on a gate somewhere else is a claim nobody can check.
//
//   A strategy with NO MODEL is out. Classic and the racing all-up are shapes:
//   they measure no edge, their tickets have no opinion in them, and every leg
//   of them is worth exactly minus the book's margin. A shape is a fine way to
//   choose how much risk you want; it is not evidence, and a "best bet" with no
//   evidence behind it is the sentence this sheet exists to avoid.
//
// HOW THEY ARE RANKED: expected log growth — what repeatedly betting this would
// do to a bankroll, the same figure `runStrategy` ranks its legs by — times a
// CERTAINTY FACTOR. The factor is 1 − the widest `fairSpread` on the ticket:
// how far the three devig methods disagree about what its legs are worth. A
// ticket whose whole edge sits inside that disagreement is a ticket the app is
// not sure of, and the ranking should say so before the punter finds out.
//
// Pure: no store, no clock of its own, no DOM. `now` and the day's entries come
// in, three rows come out.

import { expectedLogGrowth } from './engine/kelly.js';
import { isControl, proofStateOf } from './engine/proof.js';
import { money, oneIn, price } from './format.js';
import { clvFor, keyFor } from './insights.js';

/** How many make the sheet. Three is what fits on a phone above the first kick-off. */
export const TOP = 3;

/**
 * THE ONE LINE OF HONESTY UNDER THE HEADING, and it is not a disclaimer.
 *
 * "Best" is the strongest word in the app and the only place it is used. It
 * means "the most evidence", which is a comparison between three tickets and
 * not a promise about any of them, and a reader who takes it for the second
 * reading has been misled by the app rather than by themselves.
 */
export const BEST_BETS_HONESTY = 'Best bets are the ones with the strongest evidence, '
  + 'not sure things; the chance is printed on each.';

/**
 * What the sheet says on a morning nothing cleared the bar — WHICH IS THE
 * NORMAL MORNING, and the sentence has to read that way.
 *
 * The bar is two conditions and the second of them takes two hundred settled
 * legs (`engine/proof.js`), so for most of the app's life most days will print
 * nothing here. A sheet that showed empty slots, or a placeholder card, or a
 * heading with a blank under it, would read as a screen that had failed to
 * load. So the empty state is ONE QUIET LINE that says the refusal was the
 * machinery working, and the third clause is the whole difference between
 * "broken" and "finished".
 */
export const NOTHING_CLEARED = 'No best bets today. '
  + 'Nothing cleared the evidence bar; that is the bar working.';

/** The heading, in one place, because the share card prints it too. */
export const BEST_BETS_TITLE = 'Best bets today';

/**
 * THE LABEL ON THE SCREEN, and it is deliberately not that sentence.
 *
 * THE SECTION WAS NOT FINDABLE. On a morning nothing cleared — which is most
 * mornings, and will be for a long time — Today printed one line of small print
 * under the Now rule and nothing else, and the owner did not see it. A reader
 * cannot look for a thing that only appears when it has something to say.
 *
 * So the label is always on the screen, in the pad's eyebrow: two words,
 * uppercase and tracked, the same furniture every other section of the app
 * carries. The card's heading keeps the day in it because a picture sent to
 * somebody else has to say WHICH day; a label three inches under the date on
 * the screen does not.
 */
export const BEST_BETS_SECTION = 'Best bets';

/** What to do about it, when there is no reason to wait. */
export const BET_NOW = 'Bet now';

/**
 * WHERE A FAIR PRICE CAME FROM, in the words the reason sentence wants. Keyed
 * by the strategy's own model id, so a fork that swapped its model says the
 * right thing without anybody editing this table.
 */
const FAIR_SOURCE = {
  'market-consensus': 'the rest of the board',
  'sharp-anchor': 'the exchange',
  elo: 'the ratings and the board together',
  'dixon-coles': 'the goals model and the board together',
  'squiggle-ensemble': 'the tipsters and the board together',
  'user-upload': 'your own numbers and the board together',
};

/** A book's printed name, falling back to whatever key the ticket carries. */
const bookOf = (multi) => multi?.bookmaker || multi?.bookmakerKey || 'your book';

/** The widest disagreement about a leg's fair price on this ticket, or 0. */
export function ticketSpread(legs) {
  const spreads = (legs ?? [])
    .map((leg) => leg?.fairSpread)
    .filter((s) => Number.isFinite(s) && s >= 0);
  return spreads.length ? Math.max(...spreads) : 0;
}

/**
 * How sure the app is of this ticket's own numbers, as a multiplier in [0, 1].
 * One where the three devigs agree, zero where they disagree by more than the
 * whole of the fair price they are arguing about.
 */
export const certaintyOf = (legs) => Math.max(0, Math.min(1, 1 - ticketSpread(legs)));

/**
 * Has this strategy earned the right to be called a best bet on THIS ticket?
 *
 * Three answers, and they are different facts rather than degrees of one:
 *
 *   'no-model'   a control. Out, and not a failure — see the note at the top.
 *   'on-trial'   the harness half of the bar is clear (or there was never one
 *                to clear) and the forward half is not: fewer than
 *                `PROOF_MIN_LEGS` settled legs against the close, or an
 *                interval that still holds zero. It prints on the feed, marked;
 *                it is not a best bet.
 *   'unproven'   a model whose fit does not clear t. The engine would not have
 *                printed it either.
 *   null         nothing in the way.
 *
 * THE BAR IS `engine/proof.js` AND ONLY THERE. This function is the shape of
 * the question — which sports, which document — and none of the numbers.
 */
export function gateFor(strategy, legs, { fits = {}, byKey = {} } = {}) {
  if (isControl(strategy)) return 'no-model';
  const sports = [...new Set((legs ?? []).map((leg) => leg?.sport).filter(Boolean))];
  if (!sports.length) return 'unproven';
  const agg = byKey?.[keyFor(strategy?.id)] ?? null;
  const states = sports.map((sport) => proofStateOf(strategy, sport, { fits, agg }));
  if (states.includes('unproven')) return 'unproven';
  return states.every((s) => s === 'proven') ? null : 'on-trial';
}

/**
 * WHY this bet, in one sentence a person would say out loud.
 *
 * The close chaser gets its own: its claim is not that the price is generous
 * against a fair price, it is that the price is about to shorten, and saying it
 * the other way would be describing a different bet. `rawProbability` on the
 * leg is the model's own forecast before the market correction — which for this
 * model IS the expected closing probability.
 */
export function reasonFor(entry) {
  const multi = entry?.ticket?.multi ?? {};
  const legs = entry?.ticket?.legs ?? [];
  const modelId = entry?.strategy?.probabilitySource?.model ?? null;
  const book = bookOf(multi);

  if (modelId === 'close-line' && legs.length === 1 && Number.isFinite(legs[0]?.rawProbability)) {
    return `${book} pays ${price(legs[0].price)} and the close is expected at ${price(1 / legs[0].rawProbability)}`;
  }

  // THE MARKET'S FAIR PRICE, NOT THE BOOK'S OWN (the same point `pad-image.js`
  // makes at length about a mispriced single). `fairProbability` on a leg is
  // that book's quote with its own margin stripped, and measured against its
  // own price every book on earth is fair — a "best bet" built on it would
  // print "Neds pays 3.40 against a fair 3.47", which is an argument for not
  // betting. `modelProbability` is what the strategy actually claims the side
  // is worth: the board with this book left out, or the model's correction to it.
  const source = FAIR_SOURCE[modelId] ?? 'the board';
  if (legs.length === 1) {
    const fair = legs[0]?.modelProbability;
    if (!Number.isFinite(fair) || fair <= 0) return `${book} pays ${price(legs[0]?.price)}`;
    return `${book} pays ${price(legs[0].price)} against a fair ${price(1 / fair)} from ${source}`;
  }

  const fair = multi.modelProbability;
  const legWord = `${legs.length} legs`;
  if (!Number.isFinite(fair) || fair <= 0) return `${book} pays ${price(multi.combinedOdds)} on ${legWord}`;
  return `${book} pays ${price(multi.combinedOdds)} on ${legWord} against a fair `
    + `${price(1 / fair)} from ${source}`;
}

/** HOW to place it: the stake the ticket carries, the book, the price. */
export function howFor(entry) {
  const multi = entry?.ticket?.multi ?? {};
  return `${money(multi.stake)} at ${bookOf(multi)} at ${price(multi.combinedOdds)}`;
}

/**
 * WHEN to place it. "Bet now" unless something scheduled is known to be coming
 * that the market has not priced yet.
 *
 * THE HOOK IS HERE AND IT PRINTS NOTHING TODAY. F5 is the slice that stores AFL
 * team lists (Thursday 6:20 pm) and NRL lists as features, and only a harness
 * run that shows the market actually repricing after a release earns a sentence
 * telling somebody to wait. Until then `features` arrives empty and every best
 * bet says "Bet now", which is the truth.
 */
export function whenFor(entry, features = null) {
  const legs = entry?.ticket?.legs ?? [];
  for (const leg of legs) {
    const hint = features?.[leg?.matchKey]?.wait;
    if (hint?.label && hint?.at) return `Wait for ${hint.label} at ${hint.at}`;
  }
  return BET_NOW;
}

/**
 * The day's best bets, ranked, and what was left out.
 *
 * @param {object}   input
 * @param {Array}    input.entries   the day's LIVE feed entries (`timelineOf` shape)
 * @param {object}   input.fits      `${modelId}:${sport}` -> a PROVEN encompassing fit
 * @param {object}   input.byKey     the paper trail per strategy key (`insights.js` Agg)
 * @param {object}   [input.features] the wait hints, when F5 has any. Empty today.
 * @param {number}   [input.max]     how many make the sheet
 */
export function bestBets({
  entries = [],
  fits = {},
  byKey = {},
  features = null,
  max = TOP,
} = {}) {
  const ranked = [];
  const excluded = {
    noModel: 0, unproven: 0, onTrial: 0, noGrowth: 0,
  };

  for (const entry of entries ?? []) {
    const multi = entry?.ticket?.multi;
    const legs = entry?.ticket?.legs ?? [];
    if (!multi || !legs.length) continue;
    // A ticket already placed is a record, not a recommendation.
    if (multi.status && multi.status !== 'proposed') continue;

    const gate = gateFor(entry.strategy, legs, { fits, byKey });
    if (gate === 'no-model') { excluded.noModel += 1; continue; }
    if (gate === 'unproven') { excluded.unproven += 1; continue; }
    if (gate === 'on-trial') { excluded.onTrial += 1; continue; }

    const growth = expectedLogGrowth(multi.modelProbability, multi.combinedOdds);
    if (!Number.isFinite(growth) || growth <= 0) { excluded.noGrowth += 1; continue; }

    const certainty = certaintyOf(legs);
    ranked.push({
      id: entry.id ?? multi.id,
      entry,
      strategyId: multi.strategyId ?? entry.strategy?.id ?? null,
      name: entry.strategy?.name ?? multi.label ?? 'This strategy',
      growth,
      certainty,
      score: growth * certainty,
      price: multi.combinedOdds,
      stake: multi.stake,
      book: bookOf(multi),
      chance: oneIn(multi.combinedProbability),
      reason: reasonFor(entry),
      how: howFor(entry),
      when: whenFor(entry, features),
      // What this strategy's own prices have been worth against the close, with
      // the denominator and the interval on it. Null where the trail has not
      // settled enough legs to say, which is most strategies' first fortnight.
      clv: clvFor(byKey?.[keyFor(multi.strategyId ?? entry.strategy?.id, multi.tier)]) ?? null,
    });
  }

  // Score decides; a tie goes to the likelier ticket, and then to the id, so one
  // board produces one sheet however often it is drawn.
  ranked.sort((a, b) => b.score - a.score
    || (b.entry?.ticket?.multi?.combinedProbability ?? 0) - (a.entry?.ticket?.multi?.combinedProbability ?? 0)
    || String(a.id).localeCompare(String(b.id)));

  return {
    title: BEST_BETS_TITLE,
    section: BEST_BETS_SECTION,
    honesty: BEST_BETS_HONESTY,
    bets: ranked.slice(0, max).map((bet, i) => ({ ...bet, rank: i + 1 })),
    empty: ranked.length === 0,
    emptyText: NOTHING_CLEARED,
    excluded,
  };
}
